import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Edit, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { OverheadSubcategoryItem } from "@/hooks/use-overhead-categories";
import {
  useDeactivateOverheadCategory,
  useDeactivateOverheadSubcategory,
  useOverheadCategory,
  useReactivateOverheadCategory,
  useReactivateOverheadSubcategory,
  useUpdateOverheadCategoryLabel,
} from "@/hooks/use-overhead-categories";
import type { UpdateOverheadCategoryInput } from "@/schemas/overhead-categories";
import { UpdateOverheadCategorySchema } from "@/schemas/overhead-categories";
import { DeactivateOverheadEntityDialog } from "./DeactivateOverheadEntityDialog";
import {
  EditOverheadSubcategoryDialog,
  NewOverheadSubcategoryDialog,
} from "./OverheadSubcategoryDialogs";

type CatDialogState = { type: "none" } | { type: "deactivate" };
type SubcategoryDialogState =
  | { type: "none" }
  | { type: "new" }
  | { type: "edit"; subcategory: OverheadSubcategoryItem }
  | { type: "deactivate"; subcategory: OverheadSubcategoryItem };

interface OverheadCategoryDetailProps {
  categoryId: string;
  isAdmin: boolean;
}

export function OverheadCategoryDetail({ categoryId, isAdmin }: OverheadCategoryDetailProps) {
  const { data: detail, isLoading, isError, error, refetch } = useOverheadCategory(categoryId);
  const updateLabel = useUpdateOverheadCategoryLabel();
  const deactivateCategory = useDeactivateOverheadCategory();
  const reactivateCategory = useReactivateOverheadCategory();
  const deactivateSubcategory = useDeactivateOverheadSubcategory();
  const reactivateSubcategory = useReactivateOverheadSubcategory();

  const [catDialog, setCatDialog] = useState<CatDialogState>({ type: "none" });
  const [subDialog, setSubDialog] = useState<SubcategoryDialogState>({ type: "none" });
  const [showInactive, setShowInactive] = useState(false);

  const form = useForm<UpdateOverheadCategoryInput>({
    resolver: zodResolver(UpdateOverheadCategorySchema),
    defaultValues: { label: "" },
  });

  useEffect(() => {
    if (detail) form.reset({ label: detail.label });
  }, [detail, form]);

  function onSubmit(values: UpdateOverheadCategoryInput) {
    updateLabel.mutate({ id: categoryId, ...values });
  }

  if (isLoading) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <Link to="/dashboard/overhead-categories">Overhead Categories</Link>
            <ChevronRight size={14} />
            <strong>Loading…</strong>
          </div>
        </div>
        <div className="page">
          <div className="card">
            <div className="card__body space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isError || !detail) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__crumbs">
            <Link to="/dashboard/overhead-categories">Overhead Categories</Link>
            <ChevronRight size={14} />
            <strong>Error</strong>
          </div>
        </div>
        <div className="page">
          <ErrorState
            message={error?.message ?? "Failed to load overhead category"}
            onRetry={() => refetch()}
          />
        </div>
      </>
    );
  }

  const visibleSubs = showInactive
    ? detail.subcategories
    : detail.subcategories.filter((s) => s.active);
  const inactiveCount = detail.subcategories.filter((s) => !s.active).length;

  return (
    <>
      <div className="topbar">
        <div className="topbar__crumbs">
          <Link to="/dashboard/overhead-categories">Overhead Categories</Link>
          <ChevronRight size={14} />
          <strong>{detail.code}</strong>
        </div>
      </div>

      <div className="page">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/overhead-categories"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft size={16} />
              Back
            </Link>
            <span className="inline-flex items-center rounded px-2 py-0.5 text-sm font-mono font-semibold bg-muted text-foreground">
              {detail.code}
            </span>
            <h1 className="page-header__title">{detail.label}</h1>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                detail.active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"
              }`}
            >
              {detail.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Label edit form */}
        <div className="card mb-4">
          <div className="card__header">
            <h2 className="h-section">Details</h2>
          </div>
          <div className="card__body">
            <div className="mb-4">
              <p className="text-sm font-medium text-muted-foreground mb-1">Code</p>
              <span className="inline-flex items-center rounded px-2.5 py-1 text-sm font-mono font-semibold bg-muted text-foreground">
                {detail.code}
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                Overhead codes are permanent and cannot be changed.
              </p>
            </div>

            {isAdmin ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <FormField
                    control={form.control}
                    name="label"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Label</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2">
                      <Button type="submit" size="sm" disabled={updateLabel.isPending}>
                        {updateLabel.isPending ? "Saving…" : "Save Changes"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => form.reset({ label: detail.label })}
                      >
                        Reset
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      {detail.active ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCatDialog({ type: "deactivate" })}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={reactivateCategory.isPending}
                          onClick={() => reactivateCategory.mutate(categoryId)}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </Form>
            ) : (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Label</p>
                <p className="text-sm">{detail.label}</p>
              </div>
            )}
          </div>
        </div>

        {/* Subcategories */}
        <div className="card">
          <div className="card__header flex items-center justify-between">
            <h2 className="h-section">
              Subcategories{" "}
              <span className="text-muted-foreground font-normal text-sm">
                ({detail.subcategories.filter((s) => s.active).length} active)
              </span>
            </h2>
            {isAdmin && (
              <Button size="sm" onClick={() => setSubDialog({ type: "new" })}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add Subcategory
              </Button>
            )}
          </div>
          <div className="card__body p-0">
            {detail.subcategories.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                No subcategories yet.{isAdmin && " Add one to get started."}
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-24">
                        Status
                      </th>
                      {isAdmin && (
                        <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide w-40">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSubs.map((sub) => (
                      <tr
                        key={sub.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={
                              sub.active ? "text-foreground" : "text-muted-foreground line-through"
                            }
                          >
                            {sub.name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              sub.active
                                ? "bg-green-100 text-green-800"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {sub.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setSubDialog({ type: "edit", subcategory: sub })}
                                title="Rename"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {sub.active ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-muted-foreground"
                                  onClick={() =>
                                    setSubDialog({ type: "deactivate", subcategory: sub })
                                  }
                                  title="Deactivate"
                                >
                                  Off
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-muted-foreground"
                                  disabled={reactivateSubcategory.isPending}
                                  onClick={() => reactivateSubcategory.mutate(sub.id)}
                                  title="Reactivate"
                                >
                                  On
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {inactiveCount > 0 && (
                  <div className="px-4 py-2 border-t border-border">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowInactive((v) => !v)}
                    >
                      {showInactive
                        ? "Hide inactive subcategories"
                        : `Show ${inactiveCount} inactive subcategor${inactiveCount !== 1 ? "ies" : "y"}`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {catDialog.type === "deactivate" && (
        <DeactivateOverheadEntityDialog
          open
          onOpenChange={(open) => !open && setCatDialog({ type: "none" })}
          entityType="category"
          entityId={categoryId}
          entityName={`${detail.code} — ${detail.label}`}
          isPending={deactivateCategory.isPending}
          onConfirm={() =>
            deactivateCategory.mutate(categoryId, {
              onSuccess: () => setCatDialog({ type: "none" }),
            })
          }
        />
      )}

      {subDialog.type === "new" && (
        <NewOverheadSubcategoryDialog
          open
          onOpenChange={(open) => !open && setSubDialog({ type: "none" })}
          categoryId={categoryId}
          categoryCode={detail.code}
        />
      )}

      {subDialog.type === "edit" && (
        <EditOverheadSubcategoryDialog
          key={subDialog.subcategory.id}
          open
          onOpenChange={(open) => !open && setSubDialog({ type: "none" })}
          subcategory={subDialog.subcategory}
        />
      )}

      {subDialog.type === "deactivate" && (
        <DeactivateOverheadEntityDialog
          open
          onOpenChange={(open) => !open && setSubDialog({ type: "none" })}
          entityType="subcategory"
          entityId={subDialog.subcategory.id}
          entityName={subDialog.subcategory.name}
          isPending={deactivateSubcategory.isPending}
          onConfirm={() =>
            deactivateSubcategory.mutate(subDialog.subcategory.id, {
              onSuccess: () => setSubDialog({ type: "none" }),
            })
          }
        />
      )}
    </>
  );
}
